// src/services/loteError.ts
//
// Traduz o erro de uma request que pode esbarrar na trava do motor de lote na
// mensagem + variante de alerta a exibir. A trava serializa o par
// (ação, chave_pai): enquanto um lote está em voo num registro, outra operação
// sobre o mesmo registro é recusada.
//
// O caso que motivou o helper: com o ADR-048 do `api-blueltSystem`, o
// `PUT /v1/pre-ordens/{id}/cancelar` passou a recusar (400) enquanto há uma
// remoção em lote em curso na mesma pré-ordem — a mesma recusa que o
// `devolver-itens` já emitia. Sem tratamento, ela cai no mesmo alerta vermelho
// de "sem permissão para cancelar", e o usuário conclui que precisa pedir
// acesso quando na verdade tem a permissão e só precisa esperar o job terminar.
// Por isso a recusa de trava usa variante `warning` ("aguarde") em vez de
// `error` ("deu errado") — mesma lógica do 429 em `authError.ts`.
//
// POR QUE CASAR PELO TEXTO. O corpo de erro do backend é `{ error, code }`
// (`ControleExcecao.handleExceptionCustom`) — não há discriminador estruturado
// como o `reason: "rate_limited"` que o 429 carrega. O texto é a única
// informação que chega. Ele tem fonte única no servidor
// (`OperacaoLoteService.mensagemDeTravaAtiva(AcaoLote)`), está publicado no
// Swagger e tem teste lá afirmando-o, então é string de contrato e não string
// solta. Ainda assim, se um dia mudar, este helper apenas deixa de reconhecer e
// a tela volta ao comportamento anterior (vermelho com a mensagem do servidor):
// degrada, não quebra.
//
// Retorna um objeto puro (sem efeito colateral) para ser testável sem React;
// as telas passam o resultado a `showAlert`. Mesmo molde do `authError.ts`.

// Subconjunto das variantes de `showAlert`/`AlertDialog` que este helper emite.
export type LoteAlertVariant = 'error' | 'warning';

export interface LoteErrorAlert {
  message: string;
  variant: LoteAlertVariant;
}

interface ApiErrorBody {
  error?: string;
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: ApiErrorBody;
  };
}

// Trecho invariante da mensagem de trava do servidor. A ação (REMOVER,
// APROVAR, ...) aparece ANTES dele, então casar por aqui vale para qualquer
// ação sem precisar enumerá-las — e continua valendo se o motor ganhar ações
// novas. Minúsculas porque a comparação normaliza o caso.
const TRECHO_TRAVA_ATIVA = 'em andamento para este registro';

/**
 * Mensagem exibida quando a trava recusa a operação.
 *
 * É a frase que o painel da Cotação já usava para a trava detectada localmente,
 * acrescida do convite a repetir. Fica exportada para que os dois caminhos —
 * trava local e recusa do servidor — mostrem texto idêntico: para o usuário é a
 * mesma situação, e quem detectou é detalhe de implementação.
 *
 * Não reaproveitamos o texto do servidor porque ele chega sem acentuação
 * (string Java) e nomeia a ação interna do motor (`REMOVER`).
 */
export const MENSAGEM_TRAVA_ATIVA =
  'Já existe um processamento em andamento para esta cotação. Aguarde a conclusão e tente novamente em instantes.';

/**
 * Resolve a mensagem + variante de alerta para um erro de request que pode
 * esbarrar na trava de lote.
 *
 * - Recusa de trava (HTTP 400 cuja mensagem carrega o trecho invariante):
 *   variante `warning` e a mensagem de espera. O usuário tem a permissão; a
 *   operação vai funcionar assim que o lote em voo terminar.
 * - Qualquer outro erro: variante `error` e a mensagem do backend
 *   (`response.data.error`) ou o `fallback` da tela — idêntico ao que as telas
 *   faziam antes deste helper existir.
 *
 * @param error    o erro capturado no catch (shape de erro axios).
 * @param fallback mensagem padrão da tela quando o backend não envia `error`.
 */
export function resolveLoteError(error: unknown, fallback: string): LoteErrorAlert {
  const response = (error as ApiErrorLike | undefined)?.response;
  const mensagemDoServidor = typeof response?.data?.error === 'string' ? response.data.error : '';

  const isTravaAtiva =
    response?.status === 400 && mensagemDoServidor.toLowerCase().includes(TRECHO_TRAVA_ATIVA);

  if (isTravaAtiva) {
    return { message: MENSAGEM_TRAVA_ATIVA, variant: 'warning' };
  }

  return { message: mensagemDoServidor || fallback, variant: 'error' };
}
